import { endpoints } from '@/api';
import { axiosInstance } from '@/lib/utils';
import {
  buildReferralSummary,
  mapReferralActivityItem,
  mapReferralEarningsResponse,
  mapReferralHistoryResponse,
  parseReferralMeCode,
} from '@/lib/helpers/referral-api';
import type { PaginatedData, RequestResponse } from '@/types';
import type {
  ReferralActivityApiItem,
  ReferralActivityItem,
  ReferralEarningsView,
  ReferralHistoryApiData,
  ReferralHistoryView,
  ReferralListItem,
  ReferralMeCode,
  ReferralMeProfile,
  ReferralSummary,
} from '@/types/referral';
import { referralMockState, waitForReferralMock } from './mock-state';

const useReferralMockForUndocumentedEndpoints =
  import.meta.env.VITE_USE_REFERRAL_MOCK === 'true';

export const fetchReferralMe = async (): Promise<ReferralMeProfile> => {
  const response = await axiosInstance.get<RequestResponse<ReferralMeProfile>>(
    endpoints.referrals.me
  );
  return response.data.data;
};

export const fetchReferralMeCode = async (): Promise<ReferralMeCode> => {
  if (useReferralMockForUndocumentedEndpoints) {
    await waitForReferralMock();
    return {
      code: referralMockState.summary.referralCode,
      link: referralMockState.summary.referralLink,
    };
  }

  const response = await axiosInstance.get<RequestResponse<unknown>>(
    endpoints.referrals.meCode
  );
  return parseReferralMeCode(response.data.data);
};

/** @deprecated Use fetchReferralMeCode */
export const fetchReferralCode = async (): Promise<string> => {
  const { code } = await fetchReferralMeCode();
  return code;
};

export const fetchReferralSummary = async (): Promise<ReferralSummary> => {
  if (useReferralMockForUndocumentedEndpoints) {
    await waitForReferralMock();
    return referralMockState.summary;
  }

  const [meCode, earnings, history] = await Promise.all([
    fetchReferralMeCode(),
    fetchReferralEarnings(),
    fetchReferralHistory(),
  ]);

  return buildReferralSummary({
    code: meCode.code,
    link: meCode.link,
    earnings,
    history,
  });
};

/** Uses GET /referrals/history — same referral rows as the History tab. */
export const fetchReferrals = async (): Promise<ReferralListItem[]> => {
  const history = await fetchReferralHistory();
  return history.referrals;
};

export const fetchReferralActivity = async (): Promise<
  ReferralActivityItem[]
> => {
  const response = await axiosInstance.get<
    RequestResponse<PaginatedData<ReferralActivityApiItem[]>>
  >(endpoints.referrals.activity);

  const rows = response.data.data?.data ?? [];
  return rows.map(mapReferralActivityItem);
};

export const fetchReferralHistory = async (): Promise<ReferralHistoryView> => {
  const response = await axiosInstance.get<
    RequestResponse<ReferralHistoryApiData>
  >(endpoints.referrals.history);

  return mapReferralHistoryResponse(response.data.data);
};

export const fetchReferralEarnings =
  async (): Promise<ReferralEarningsView> => {
    if (useReferralMockForUndocumentedEndpoints) {
      await waitForReferralMock();
      const entries = referralMockState.earnings;
      return {
        entries,
        availableBalance: referralMockState.summary.availableBalance,
        totalEarnings: referralMockState.summary.totalEarnings,
        thisMonthEarnings: referralMockState.summary.thisMonthEarnings,
      };
    }

    const response = await axiosInstance.get<RequestResponse<unknown>>(
      endpoints.referrals.earnings
    );

    return mapReferralEarningsResponse(response.data.data);
  };
